import {
  ColumnDataType,
  GetOutputSchemasParams,
  Provider,
  QueryAndOutputs,
  TableSchemasList,
  WorkflowCodeType,
  WorkflowComponentCodeParams,
  WorkflowComponentConditionCodeParams,
  WorkflowComponentOutputTablesParams,
  WorkflowComponentOutputType,
  WorkflowComponentParamType
} from '../types'

import dedent from 'ts-dedent'
import { IF_OUTPUT_NAME, ELSE_OUTPUT_NAME, hashString, nop } from '../utils'
import { WorkflowComponent } from './common/workflowcomponent'
import { DBricksConnection } from '../workflow/connections/databricks'

const SUCCESS_OUTPUT_NAME = 'success'
const ERROR_OUTPUT_NAME = 'error'

class SuccessError extends WorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.successerror',
        group: 'Control',
        title: 'Success/Error Split',
        description: `
          This component evaluates the outcome of the previous operation and splits the workflow into 'Success' and 'Error' branches.
        `,
        starred: true
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        }
      ],
      [
        {
          name: IF_OUTPUT_NAME,
          description: 'Success table',
          type: WorkflowComponentOutputType.Table,
          title: SUCCESS_OUTPUT_NAME
        },
        {
          name: ELSE_OUTPUT_NAME,
          description: 'Error table',
          type: WorkflowComponentOutputType.Table,
          title: ERROR_OUTPUT_NAME
        }
      ]
    )
  }

  async getFullCodeSnippet({ params }: WorkflowComponentCodeParams): Promise<QueryAndOutputs> {
    return {
      query: '',
      outputs: {}
    }
  }

  async getCode(_: WorkflowComponentCodeParams): Promise<QueryAndOutputs> {
    return {
      query: '',
      outputs: {}
    }
  }

  getOutputTables({
    workflow,
    params,
    connection,
    componentName,
    codeType
  }: WorkflowComponentOutputTablesParams): Record<string, string> {
    const tableError = connection.getOutputTableName(workflow.id, componentName, params, ELSE_OUTPUT_NAME, codeType)
    return {
      [IF_OUTPUT_NAME]: params.source as string,
      [ELSE_OUTPUT_NAME]: tableError
    }
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  _getOutputSchemas({
    workflow,
    params,
    schemas,
    connection,
    componentName
  }: GetOutputSchemasParams): TableSchemasList {
    const columns = this._getSchemaForTablename(params.source as string, schemas, connection)
    const fqnSuccess = params.source as string
    const fqnError = this._getDryRunOutputTableFqn(workflow, params, connection, componentName, ELSE_OUTPUT_NAME)
    return [
      {
        fqn: fqnSuccess,
        schema: columns
      },
      {
        fqn: fqnError,
        schema: [
          {
            name: 'error',
            type: ColumnDataType.String
          }
        ]
      }
    ]
  }

  async getConditionCode({
    workflow,
    params,
    envVars,
    componentName,
    connection,
    ifQuery,
    elseQuery,
    codeType,
    schemas
  }: WorkflowComponentConditionCodeParams): Promise<string> {
    if (codeType === WorkflowCodeType.DryRun) {
      return ''
    }
    const upstream = workflow.componentsInmediatelyUpstream(componentName)
    if (upstream.length === 0) {
      return ''
    }
    const upstreamComponent = upstream[0]

    const queries = await workflow.getSubqueries(connection, envVars, codeType, schemas)
    const upstreamComponentQuery = queries.find((q) => q.componentName === upstreamComponent)?.query

    const varName = `error_${hashString(upstreamComponent)}`
    const tablenameError = connection.getOutputTableName(workflow.id, componentName, params, ELSE_OUTPUT_NAME, codeType)
    const varPrefix = connection.type === Provider.Snowflake ? ':' : ''
    const errorQuery = connection.getCreateTempTableQuery(
      tablenameError,
      `SELECT ${varPrefix}${varName} AS error`,
      codeType,
      null
    )

    ifQuery ||= nop(connection.type)
    elseQuery ||= nop(connection.type)

    switch (connection.type) {
      case Provider.BigQuery:
        return dedent`
          BEGIN
            DECLARE
              ${varName} STRING DEFAULT NULL;
            BEGIN
              ${upstreamComponentQuery}
            EXCEPTION WHEN ERROR THEN
              SET ${varName} = @@error.message;
            END;
            IF
              ${varName} IS NULL
            THEN
              ${ifQuery}
            ELSE
              ${errorQuery}
              ${elseQuery}
            END IF;
          END;`
      case Provider.Snowflake:
        return dedent`
          DECLARE
            ${varName} STRING DEFAULT NULL;
          BEGIN
            BEGIN
              ${upstreamComponentQuery}
            EXCEPTION WHEN OTHER THEN
              ${varName} := SQLERRM;
            END;
            IF
              (${varName} IS NULL)
            THEN
              ${ifQuery}
            ELSE
              ${errorQuery}
              ${elseQuery}
            END IF;
          END;`
      case Provider.Postgres:
      case Provider.Redshift:
        return dedent`
          DECLARE
            ${varName} VARCHAR DEFAULT NULL;
          BEGIN
            BEGIN
              ${upstreamComponentQuery}
            EXCEPTION WHEN OTHERS THEN
              ${varName} := SQLERRM;
            END;
            IF
              (${varName} IS NULL)
            THEN
              ${ifQuery}
            ELSE
              ${errorQuery}
              ${elseQuery}
            END IF;
          END;`
      case Provider.DatabricksRest: {
        const fstring = true
        const errorQueryDatabricks = (connection as DBricksConnection).wrapSQLCodeAsPythonCall(
          connection.getCreateTempTableQuery(tablenameError, `SELECT """{${varName}}""" AS error`, codeType, null),
          fstring
        )
        return dedent`
          ${varName} = None
          try:
            ${upstreamComponentQuery}
          except Exception as e:
            ${varName} = e.args[0] if e.args else str(e)
          if ${varName} is None:
            ${ifQuery}
          else:
            ${errorQueryDatabricks}
            ${elseQuery}`
      }
      default:
        throw new Error(`Unsupported connection type: ${connection.type}`)
    }
  }
  supportedProviders(): Provider[] {
    return [Provider.BigQuery, Provider.Postgres, Provider.Snowflake, Provider.DatabricksRest]
  }
}

export { SuccessError }
